import dayjs, { type Dayjs } from 'dayjs';

const TIMESTAMP_PATTERN = /^\d{10,13}$/;

/**
 * 将 dayjs/Date/字符串/数字 转为接口所需的时间戳（毫秒）
 * 用于 next_run_time、expire_time、create_time、update_time
 */
export const toSchedulerTimestamp = (
  value?: Dayjs | Date | string | number | null,
): number | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return undefined;
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'object' && 'valueOf' in value) {
    const ms = (value as Dayjs | Date).valueOf();
    return Number.isNaN(ms) ? undefined : ms;
  }
  const d = dayjs(value as string);
  return d.isValid() ? d.valueOf() : undefined;
};
/** 接口时间戳统一展示格式（不显示秒） */
export const SCHEDULER_TIMESTAMP_FORMAT = 'YYYY/MM/DD HH:mm';

/**
 * 将时间戳（秒/毫秒）、Date、ISO 字符串格式化为 YYYY-MM-DD HH:mm:ss
 * 用于 create_time、expire_time、next_run_time、update_time 等字段的展示
 */
export const formatSchedulerTimestamp = (
  value?: string | number | Date | null,
): string => {
  if (value === null || value === undefined) {
    return '-';
  }
  let ms: number | null = null;
  if (typeof value === 'number') {
    ms = Number.isFinite(value) ? (value < 1e12 ? value * 1000 : value) : null;
  } else if (value instanceof Date) {
    ms = value.getTime();
  } else {
    const text = String(value).trim();
    if (TIMESTAMP_PATTERN.test(text)) {
      ms = text.length === 10 ? Number(text) * 1000 : Number(text);
    } else {
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed.format(SCHEDULER_TIMESTAMP_FORMAT) : '-';
    }
  }
  if (ms === null) return '-';
  const formatted = dayjs(ms);
  return formatted.isValid() ? formatted.format(SCHEDULER_TIMESTAMP_FORMAT) : '-';
};

/**
 * 解析接口返回的时间字段（支持时间戳毫秒/秒 或 ISO 字符串）
 * 用于 detail 返回的 next_run_time、expire_time、create_time、update_time
 */
export const parseSchedulerDateTime = (
  value?: string | number | Date | null,
): Dayjs | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    const ms = Number.isNaN(value) ? NaN : value < 1e12 ? value * 1000 : value;
    const d = dayjs(ms);
    return d.isValid() ? d : null;
  }
  if (typeof value === 'object' && 'valueOf' in value) {
    const d = dayjs(value as Date);
    return d.isValid() ? d : null;
  }
  const d = dayjs(value as string);
  return d.isValid() ? d : null;
};

const normalizeTimestamp = (value: string): number | null => {
  if (!TIMESTAMP_PATTERN.test(value)) {
    return null;
  }

  if (value.length === 10) {
    return Number(value) * 1000;
  }

  if (value.length === 13) {
    return Number(value);
  }

  return null;
};

export const formatSchedulerFirstRunTime = (
  value?: string | number | null,
): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  const text = String(value).trim();
  if (!text) {
    return '-';
  }

  const timestamp = normalizeTimestamp(text);
  if (timestamp === null) {
    return text;
  }

  const formatted = dayjs(timestamp);
  if (!formatted.isValid()) {
    return text;
  }

  return formatted.format(SCHEDULER_TIMESTAMP_FORMAT);
};
